import React, { useContext } from 'react';
import StyledTable from '@shared/components/StyledTable';
import CustomSelector from '@shared/components/CustomSelector';
import CustomDatePicker from '@shared/components/CustomDatePicker';
import MuiMultiSelectionDropDown from '@shared/components/MuiMultiSelectionDropDown';
import CONTRACT_STATUS_OPTIONS from './constants/contractStatusOptions';
import STATUS_TYPES from './constants/statusTypes';
import CONTRACT_STATUSES from './constants/contractStatuses';
import TABLE_FIELD_NAMES from './constants/tableFieldNames';
import moment from 'moment';
import cx from 'classnames';
import { EventTypeContext } from '../../../shared/contexts/EventTypeContext';
import { EventSettingsContext } from '../../../shared/contexts/EventSettingsContext';

const SCROLL_VERTICAL = 300;
const EMPTY_SELECTED_OPTION = 'N/A';
const CONTRACT_STATUSES_WITHOUT_CONTRACT_DATE = [
  CONTRACT_STATUSES.PENDING_CREATION,
  CONTRACT_STATUSES.NA
];

const LABEL_BY_STATUS_TYPE = {
  [STATUS_TYPES.CUSTOMER]: (_, { orgUnitNames }) =>
    `Customer - ${orgUnitNames?.sort().join(', ') || EMPTY_SELECTED_OPTION}`,
  [STATUS_TYPES.ATL]: (_, { atlName }) => `ATL - ${atlName || EMPTY_SELECTED_OPTION}`,
  [STATUS_TYPES.ATL_OBSERVER]: (_, { atlObserverName }) =>
    `ATL Observer - ${atlObserverName || EMPTY_SELECTED_OPTION}`,
  [STATUS_TYPES.ATM_OBSERVER]: record => `ATM Observer - ${record.atmName}`,
  [STATUS_TYPES.ATM]: record => `${record.atmType} - ${record.atmName}`
};

/**
 * @typedef {object} SowStatusTableProps
 * @property {boolean} [loading]
 * @property {Array<*>} [data]
 * @property {string} [atlName]
 * @property {string} [atlObserverName]
 * @property {Array<*>} [orgUnitNames]
 * @property {Date} [eventStartDate]
 * @property {(eventFormData: Object | ((prev: Object) => Object)) => void} setEventFormData
 * @property {Array<*>} [sowStatusesErrors]
 */

/**
 * @param {SowStatusTableProps} props
 */
const SowStatusTable = props => {
  const {
    loading = false,
    data = [],
    eventStartDate,
    atlName,
    atlObserverName,
    orgUnitNames,
    setEventFormData,
    sowStatusesErrors = []
  } = props;
  const eventSettings = useContext(EventSettingsContext);
  const eventTypeDataConfig = useContext(EventTypeContext);
  const contractTypeOptions = eventTypeDataConfig.contractTypeOptions;
  const editOnlyQualifiedAtms = eventSettings.sowSettings.editOnlyQualifiedAtms;

  const setContractData = (id, fieldName, value) => {
    const updatedSowStatuses = [...data];
    const updatedSowStatues = updatedSowStatuses.find(status => status.id === id);
    updatedSowStatues[fieldName] = value;

    if (shouldResetDateValue(fieldName, value)) {
      updatedSowStatues[TABLE_FIELD_NAMES.CONTRACT_DATE] = null;
    }

    setEventFormData(prev => ({
      ...prev,
      sowStatuses: updatedSowStatuses
    }));
  };

  const columns = [
    {
      title: '',
      width: '30%',
      render: (_, record) => {
        const handler = LABEL_BY_STATUS_TYPE[record.statusType];
        return handler?.(record, {
          orgUnitNames,
          atlName,
          atlObserverName
        });
      }
    },
    {
      title: 'Contract Type',
      dataIndex: TABLE_FIELD_NAMES.CONTRACT_TYPES,
      width: '30%',
      render: (value, record, index) => (
        <MuiMultiSelectionDropDown
          data-testid={`contractTypes${record.id}`}
          id={`contractTypes${record.id}`}
          inputLabel='Select'
          className={cx(
            'sow-contract-type-selection',
            hasError(sowStatusesErrors, index, TABLE_FIELD_NAMES.CONTRACT_TYPES)
          )}
          selectedValues={
            value?.map(val => contractTypeOptions.find(option => option.value === val)) || []
          }
          options={contractTypeOptions}
          setSelectedValue={newValues => {
            setContractData(
              record.id,
              TABLE_FIELD_NAMES.CONTRACT_TYPES,
              newValues.map(newValue => newValue.value)
            );
          }}
          limitTags={3}
          disabled={isRowDisabled(record.statusType, editOnlyQualifiedAtms)}
        />
      )
    },
    {
      title: 'Contract Status',
      dataIndex: TABLE_FIELD_NAMES.CONTRACT_STATUS,
      width: '20%',
      render: (value, record, index) => (
        <CustomSelector
          data-testid={`contractStatus${record.id}`}
          name={`contractStatus${record.id}`}
          placeholder='Select'
          options={CONTRACT_STATUS_OPTIONS}
          className={hasError(sowStatusesErrors, index, TABLE_FIELD_NAMES.CONTRACT_STATUS)}
          value={value}
          setValue={newValue => {
            setContractData(record.id, TABLE_FIELD_NAMES.CONTRACT_STATUS, newValue);
          }}
          disabled={isRowDisabled(record.statusType, editOnlyQualifiedAtms)}
        />
      )
    },
    {
      title: 'Date',
      dataIndex: TABLE_FIELD_NAMES.CONTRACT_DATE,
      width: '20%',
      render: (value, record) => (
        <CustomDatePicker
          id={`sowDate${record.id}`}
          maxDate={
            record.statusType === STATUS_TYPES.CUSTOMER
              ? moment(eventStartDate).subtract(1, 'days').toDate()
              : null
          }
          selectedDate={value}
          setSelectedDate={newValue => {
            setContractData(record.id, TABLE_FIELD_NAMES.CONTRACT_DATE, newValue);
          }}
          popperProps={{ strategy: 'fixed' }}
          disabled={
            CONTRACT_STATUSES_WITHOUT_CONTRACT_DATE.includes(
              record[TABLE_FIELD_NAMES.CONTRACT_STATUS]
            ) || isRowDisabled(record.statusType, editOnlyQualifiedAtms)
          }
        />
      )
    }
  ];

  return (
    <StyledTable
      rowKey='id'
      dataSource={data}
      columns={columns}
      pagination={false}
      showSorterTooltip={false}
      loading={loading}
      scroll={{ y: SCROLL_VERTICAL }}
    />
  );
};

const isRowDisabled = (statusType, editOnlyQualifiedAtms) => {
  return editOnlyQualifiedAtms && statusType !== STATUS_TYPES.ATM;
};

const hasError = (errors, index, field) => (errors?.[index]?.[field] ? 'hasError' : '');

const shouldResetDateValue = (fieldName, value) =>
  fieldName === TABLE_FIELD_NAMES.CONTRACT_STATUS &&
  CONTRACT_STATUSES_WITHOUT_CONTRACT_DATE.includes(value);

export default SowStatusTable;
